import { useCallback, useEffect, useState } from "react";
import { adminAnalytics } from "../lib/api";

const RANGES = [
  { days: 1, label: "24h" },
  { days: 7, label: "7d" },
  { days: 30, label: "30d" },
  { days: 90, label: "90d" },
];

const TYPE_LABELS = {
  pageview: "page views",
  outbound: "outbound clicks",
  concierge_ask: "concierge questions",
  search: "searches",
  upvote: "upvotes",
  terminal_create: "terminals created",
  terminal_add: "tools added to terminal",
  terminal_remove: "tools removed",
  cmdk_open: "⌘K opens",
  stack_create: "stacks created",
  tool_view: "tool views",
};

function Stat({ label, value, testid }) {
  return (
    <div className="border border-black p-3" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-wide text-[#888]">{label}</div>
      <div className="text-2xl font-bold tabular-nums mt-1">{value}</div>
    </div>
  );
}

// Tiny inline bar (no chart lib) — keeps the brutalist look.
function Bar({ value, max }) {
  const pct = max > 0 ? Math.max(2, Math.round((value / max) * 100)) : 0;
  return (
    <span
      className="inline-block h-[10px] bg-black align-middle"
      style={{ width: `${pct}%` }}
      aria-hidden="true"
    />
  );
}

function Panel({ title, children, testid }) {
  return (
    <div className="border border-black p-3" data-testid={testid}>
      <div className="text-[12px] font-bold mb-2">{title}</div>
      {children}
    </div>
  );
}

export default function AdminAnalytics() {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      setData(await adminAnalytics(days));
    } catch {
      setErr("could not load analytics");
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    load();
  }, [load]);

  const byType = (data?.by_type || []).reduce((m, r) => ({ ...m, [r.type]: r.n }), {});
  const maxOutbound = Math.max(1, ...(data?.top_outbound || []).map((r) => r.clicks));
  const maxPage = Math.max(1, ...(data?.top_pages || []).map((r) => r.n));
  const maxSurface = Math.max(1, ...(data?.outbound_by_surface || []).map((r) => r.n));
  const maxSeries = Math.max(1, ...(data?.series || []).map((r) => r.total));

  return (
    <div className="space-y-4" data-testid="admin-analytics">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[12px] text-[#666]">range:</span>
        {RANGES.map((r) => (
          <button
            key={r.days}
            onClick={() => setDays(r.days)}
            className={`tv-cat-pill ${days === r.days ? "active" : ""}`}
            data-testid={`analytics-range-${r.label}`}
          >
            [{r.label}]
          </button>
        ))}
        <button className="tv-cat-pill ml-auto" onClick={load} data-testid="analytics-refresh">
          [refresh]
        </button>
      </div>

      {err && <div className="text-[13px] text-[#a00]" data-testid="analytics-error">{err}</div>}
      {loading ? (
        <div className="text-[13px] text-[#888] py-6" data-testid="analytics-loading">loading…</div>
      ) : data ? (
        <>
          {/* Top-line stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <Stat label="unique visitors" value={data.unique_visitors} testid="stat-visitors" />
            <Stat label="total events" value={data.total_events} testid="stat-events" />
            <Stat label="outbound clicks" value={data.outbound_total} testid="stat-outbound" />
            <Stat label="concierge Qs" value={byType.concierge_ask || 0} testid="stat-concierge" />
          </div>

          {/* Daily trend */}
          <Panel title={`daily activity (last ${data.window_days}d)`} testid="panel-series">
            {(data.series || []).length === 0 ? (
              <div className="text-[12px] text-[#888]">no activity yet.</div>
            ) : (
              <div className="space-y-1">
                {data.series.map((r) => (
                  <div key={r.day} className="flex items-center gap-2 text-[11px]">
                    <span className="w-[78px] text-[#888] tabular-nums">{r.day.slice(5)}</span>
                    <span className="flex-1"><Bar value={r.total} max={maxSeries} /></span>
                    <span className="w-[120px] text-right tabular-nums">
                      {r.total} <span className="text-[#999]">({r.outbound} out)</span>
                    </span>
                  </div>
                ))}
              </div>
            )}
          </Panel>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Top outbound tools w/ section breakdown */}
            <Panel title="top outbound tools → third-party" testid="panel-outbound">
              {(data.top_outbound || []).length === 0 ? (
                <div className="text-[12px] text-[#888]">no outbound clicks yet.</div>
              ) : (
                <table className="w-full text-[12px]">
                  <thead>
                    <tr className="text-[#888] text-left">
                      <th className="font-normal py-1">tool</th>
                      <th className="font-normal py-1 text-right">clicks</th>
                      <th className="font-normal py-1 pl-3">by section</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.top_outbound.map((r) => (
                      <tr key={r.slug} className="border-t border-[#eee]" data-testid={`outbound-${r.slug}`}>
                        <td className="py-1 pr-2 font-semibold break-words">{r.title || r.slug}</td>
                        <td className="py-1 text-right tabular-nums">
                          <Bar value={r.clicks} max={maxOutbound} /> {r.clicks}
                        </td>
                        <td className="py-1 pl-3 text-[11px] text-[#666]">
                          {Object.entries(r.by_surface)
                            .sort((a, b) => b[1] - a[1])
                            .map(([s, n]) => `${s}:${n}`)
                            .join("  ")}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Panel>

            {/* Outbound by surface + event mix */}
            <div className="space-y-4">
              <Panel title="outbound clicks by section" testid="panel-surfaces">
                {(data.outbound_by_surface || []).length === 0 ? (
                  <div className="text-[12px] text-[#888]">no data yet.</div>
                ) : (
                  <div className="space-y-1">
                    {data.outbound_by_surface.map((r) => (
                      <div key={r.surface} className="flex items-center gap-2 text-[11px]">
                        <span className="w-[90px] text-[#666]">{r.surface}</span>
                        <span className="flex-1"><Bar value={r.n} max={maxSurface} /></span>
                        <span className="w-[40px] text-right tabular-nums">{r.n}</span>
                      </div>
                    ))}
                  </div>
                )}
              </Panel>

              <Panel title="event mix" testid="panel-types">
                {(data.by_type || []).length === 0 ? (
                  <div className="text-[12px] text-[#888]">no events yet.</div>
                ) : (
                  <div className="space-y-1 text-[11px]">
                    {data.by_type.map((r) => (
                      <div key={r.type} className="flex justify-between">
                        <span className="text-[#666]">{TYPE_LABELS[r.type] || r.type}</span>
                        <span className="tabular-nums font-semibold">{r.n}</span>
                      </div>
                    ))}
                  </div>
                )}
              </Panel>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Top pages */}
            <Panel title="top pages" testid="panel-pages">
              {(data.top_pages || []).length === 0 ? (
                <div className="text-[12px] text-[#888]">no page views yet.</div>
              ) : (
                <div className="space-y-1">
                  {data.top_pages.map((r) => (
                    <div key={r.path || "—"} className="flex items-center gap-2 text-[11px]">
                      <span className="flex-1 truncate" title={r.path}>{r.path || "—"}</span>
                      <span className="w-[80px]"><Bar value={r.n} max={maxPage} /></span>
                      <span className="w-[40px] text-right tabular-nums">{r.n}</span>
                    </div>
                  ))}
                </div>
              )}
            </Panel>

            {/* Top questions + searches */}
            <div className="space-y-4">
              <Panel title="top concierge questions" testid="panel-queries">
                {(data.top_queries || []).length === 0 ? (
                  <div className="text-[12px] text-[#888]">no questions yet.</div>
                ) : (
                  <ol className="space-y-1 text-[11px] list-decimal pl-4">
                    {data.top_queries.map((r, i) => (
                      <li key={i} className="break-words">
                        <span className="text-[#999] tabular-nums mr-1">{r.n}×</span> {r.q}
                      </li>
                    ))}
                  </ol>
                )}
              </Panel>
              <Panel title="top searches" testid="panel-searches">
                {(data.top_searches || []).length === 0 ? (
                  <div className="text-[12px] text-[#888]">no searches yet.</div>
                ) : (
                  <ol className="space-y-1 text-[11px] list-decimal pl-4">
                    {data.top_searches.map((r, i) => (
                      <li key={i} className="break-words">
                        <span className="text-[#999] tabular-nums mr-1">{r.n}×</span> {r.q}
                      </li>
                    ))}
                  </ol>
                )}
              </Panel>
            </div>
          </div>

          <div className="text-[11px] text-[#999]">
            Anonymous, first-party analytics. No raw IPs or PII stored. Admin page
            views are excluded.
          </div>
        </>
      ) : null}
    </div>
  );
}
